import React, { FC, HTMLProps, ReactElement } from 'react';

import IngeregeldXs from '@/assets/icons/IngeregeldXs';
import {
  GewijzigdeInzetSignaal,
  InzetbaarheidSignaal,
  NieuweClaimSignaal,
  OmleidingSignaal,
  OpheffenSignaal,
  Signaal,
  VerlengenSignaal,
  VersterkenSignaal,
  VerwijderdeClaimSignaal,
  VerzwakSignaal,
  WisselenSignaal,
} from '@/types/Signalen';
import { dateWithDayOfWeekWithTime } from '@/utils/dateUtils';

import { InfoElement, signaalInfo } from './signaalTeksten';

import styles from './Signalen.module.css';

export type SignaalPositie = 'enkelvoudig' | 'eerste' | 'middelste' | 'laatste';

export enum signaalTypes {
  CLAIM = 'Claim',
  GEWIJZIGD = 'Gewijzigd',
  INGELEGD = 'Ingelegd',
  INGEREGELD = 'Ingeregeld',
  OMGELEID = 'Omgeleid',
  OPGEHEVEN = 'Opgeheven',
  VERSTERKT = 'Versterkt',
  VERWIJDERD = 'Verwijderd',
  VERZWAKT = 'Verzwakt',
  WISSEL = 'Wissel',
  INZETBAARHEID = 'Inzetbaarheid',
  VERLENGD = 'Verlengd',
}

export const getSignaalType: (signaal: Signaal) => string = (signaal) => {
  switch (signaal?.type) {
    case 'GewijzigdeInzetSignaal':
      return signaalTypes.GEWIJZIGD;
    case 'InregelingSignaal':
      return signaalTypes.INGEREGELD;
    case 'NieuweBewegingSignaal':
      return signaalTypes.INGELEGD;
    case 'OmleidingSignaal':
      return signaalTypes.OMGELEID;
    case 'OpheffenSignaal':
      return signaalTypes.OPGEHEVEN;
    case 'VersterkenSignaal':
      return signaalTypes.VERSTERKT;
    case 'NieuweClaimSignaal':
      return signaalTypes.CLAIM;
    case 'VerzwakSignaal':
      return signaalTypes.VERZWAKT;
    case 'VerwijderdeClaimSignaal':
      return signaalTypes.VERWIJDERD;
    case 'WisselenSignaal':
      return signaalTypes.WISSEL;
    case 'InzetbaarheidSignaal':
      return signaalTypes.INZETBAARHEID;
    case 'VerlengenSignaal':
      return signaalTypes.VERLENGD;
  }
};

/**
 * Bepaal per signaal type of de eenheid ingeregeld is
 */
export const isIngeregeldSignaal = (signaal: Signaal): boolean => {
  switch (signaal.type) {
    case 'InregelingSignaal':
      return true;
    case 'GewijzigdeInzetSignaal':
      return !!(signaal as GewijzigdeInzetSignaal).betrokkenWerklijn?.inregelDatum;
    case 'OmleidingSignaal':
      return (signaal as OmleidingSignaal).betrokkenWerklijnen?.some((werklijn) => werklijn?.inregelDatum) ?? false;
    case 'OpheffenSignaal':
      return (signaal as OpheffenSignaal).betrokkenWerklijnen?.some((werklijn) => werklijn?.inregelDatum) ?? false;
    case 'VersterkenSignaal':
      return !!(signaal as VersterkenSignaal).versterkteWerklijn?.inregelDatum;
    case 'NieuweClaimSignaal':
      return !!(signaal as NieuweClaimSignaal).betrokkenWerklijn?.inregelDatum;
    case 'VerzwakSignaal':
      return !!(signaal as VerzwakSignaal).verzwakteWerklijn?.inregelDatum;
    case 'VerwijderdeClaimSignaal':
      return !!(signaal as VerwijderdeClaimSignaal).betrokkenWerklijn?.inregelDatum;
    case 'WisselenSignaal':
      return !!(signaal as WisselenSignaal).betrokkenWerklijn?.inregelDatum;
    case 'VerlengenSignaal':
      return (signaal as VerlengenSignaal).betrokkenWerklijnen?.some((werklijn) => werklijn?.inregelDatum) ?? false;
    case 'InzetbaarheidSignaal':
      return !!(signaal as InzetbaarheidSignaal).inregelDatum;
    default:
      return false;
  }
};

/**
 * @description Geef de actor van een signaal.
 */
const getSignalActor = (signaal: Signaal, full?: boolean): string => {
  const fullDescription = full && signaal.gebruiker?.functie ? ` - ${signaal.gebruiker.functie}` : '';
  const sub = signaal.gebruiker?.id ? ` - ${signaal.gebruiker.id.substring(signaal.gebruiker.id.length - 4)}` : '';
  return signaal.actor === 'Bam_client' && signaal.gebruiker?.afdeling
    ? signaal.actor.replace('Bam_client', `${signaal.gebruiker.afdeling}${fullDescription}${sub}`)
    : signaal.actor;
};
const SignalenInfo: FC<{ info: InfoElement[]; ingeregeldSignaal: boolean; signaal: Signaal }> = ({
  info,
  ingeregeldSignaal,
  signaal,
}): ReactElement => {
  return (
    <>
      {info.map((info) => (
        <span key={info?.elemId} className={styles.signaalInfoElement} data-infotype={info?.type}>
          {['eenheid', 'betrokkenWerklijnen', 'eenheid_misc'].includes(info?.type) && ingeregeldSignaal ? (
            <>
              <IngeregeldXs
                data-testid={`signaal_${signaal.berichtId}_${signaal.indexInBericht}_icon`}
                className={styles.signaalIconStyle}
                width='16px'
                height='16px'
                aria-hidden='true'
              />
              {info?.tekst}
            </>
          ) : (
            info?.tekst
          )}
        </span>
      ))}
    </>
  );
};

const SignaalRow: FC<
  {
    signaal: Signaal;
    positie: SignaalPositie;
    handleOnSignaalClick?: (event) => void;
    handleOnKeyDown?: (event) => void;
  } & HTMLProps<HTMLDivElement>
> = ({ signaal, handleOnSignaalClick, handleOnKeyDown, positie, ...rest }) => {
  const signaalType = getSignaalType(signaal);
  const infoElementen: InfoElement[] = signaalInfo(signaal);
  const ingeregeldSignaal = isIngeregeldSignaal(signaal);
  const ingeregeldText = ingeregeldSignaal ? ' (ingeregelde eenheid)' : '';
  const titleText = `${signaalType}${ingeregeldText}:\n${infoElementen.map((e) => e.tekst).join('\n')}\n(${getSignalActor(signaal, true)})`;
  return (
    <div
      className={styles.signaalRowStyle}
      data-positie={positie}
      onKeyDown={(event) => handleOnKeyDown?.(event)}
      onClick={(event) => handleOnSignaalClick?.(event)}
      onContextMenu={(event) => handleOnSignaalClick?.(event)}
      data-testid={`signaal_${signaal.berichtId}_${signaal.indexInBericht}`}
      tabIndex={0}
      {...rest}
    >
      <div className={styles.signaalContentDivStyle} title={`${titleText}`}>
        <span className={styles.signaalTypeStyle}>
          <span>{signaalType}</span>
        </span>
        <div className={styles.signaalInfoStyle}>
          <SignalenInfo info={infoElementen} ingeregeldSignaal={ingeregeldSignaal} signaal={signaal} />
        </div>
        <span className={styles.signaalActorStyle} data-wts={signaal.actor === 'WTS' && 'yes'}>
          {getSignalActor(signaal)}
        </span>
        <div className={styles.timeDivStyle}>
          <span className={styles.timeSpanStyle}>{dateWithDayOfWeekWithTime(signaal.tijdstip)}</span>
        </div>
      </div>
    </div>
  );
};

export default SignaalRow;
